import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';

export default class extends Controller {
  static targets = [
    'form',
    'paymentMethod',
    'retainers',
    'amount',
    'sendReceipt',
    'receiptMessage',
    'receiptMessageInput',
    'previewButton',
    'formPane',
    'previewPane',
    'previewIframe',
  ];

  static values = {
    previewUrl: String,
    invoiceId: Number,
  };

  connect() {
    this.#syncPaymentMethod({ resetAmount: false });
    this.#syncReceipt();
  }

  paymentMethodChanged() {
    this.#syncPaymentMethod({ resetAmount: true });
  }

  retainerChanged(event) {
    this.#applyRetainer(event.target);
  }

  receiptChanged() {
    this.#syncReceipt();
  }

  receiptMessageInputTargetConnected() {
    this.#notifyPresets();
  }

  messageChanged() {
    this.#notifyPresets();
  }

  async preview(event) {
    event.preventDefault();

    const body = new FormData(this.formTarget);
    body.set('validate_only', '1');

    const request = new FetchRequest(this.formTarget.method, this.formTarget.action, {
      body,
      responseKind: 'turbo-stream',
    });
    const response = await request.perform();

    if (!response.ok) {
      return;
    }

    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set('invoice_id', this.invoiceIdValue);
    url.searchParams.set('preview_invoice_payment', '1');
    url.searchParams.set('invoice_payment[payment_method]', this.paymentMethodTarget.value);
    const retainer = this.retainersTarget.querySelector('.payment-retainer-id:checked');
    if (retainer) {
      url.searchParams.set('invoice_payment[retainer_id]', retainer.value);
    }
    url.searchParams.set(
      'invoice_payment[amount]',
      response.headers.get('X-Invoice-Payment-Amount')
    );
    url.searchParams.set('invoice_payment[date]', response.headers.get('X-Invoice-Payment-Date'));
    url.searchParams.set(
      'invoice_payment[custom_message]',
      this.receiptMessageInputTarget.value.replace(/\r?\n/g, '<br>')
    );

    this.previewIframeTarget.src = url.toString();
    this.formPaneTarget.style.display = 'none';
    this.previewPaneTarget.classList.remove('hide');
  }

  backFromFooter(event) {
    if (!event.target.closest('[data-invoice-payment-back]')) {
      return;
    }

    this.previewIframeTarget.src = '';
    this.previewPaneTarget.classList.add('hide');
    this.formPaneTarget.style.display = '';
  }

  #syncPaymentMethod({ resetAmount }) {
    const useRetainer = this.paymentMethodTarget.value === 'retainer';
    this.retainersTarget.classList.toggle('hide', !useRetainer);

    if (!resetAmount) {
      return;
    }

    this.amountTarget.value = '';

    if (useRetainer) {
      this.amountTarget.disabled = true;

      const firstRetainer = this.retainersTarget.querySelector('.payment-retainer-id');
      if (firstRetainer) {
        firstRetainer.checked = true;
        this.#applyRetainer(firstRetainer);
      }
    } else {
      this.retainersTarget
        .querySelectorAll('.payment-retainer-id')
        .forEach((retainer) => (retainer.checked = false));
      this.amountTarget.disabled = false;
    }
  }

  #applyRetainer(retainer) {
    this.amountTarget.disabled = false;
    this.amountTarget.value = retainer.dataset.amount || '';
  }

  #syncReceipt() {
    if (!this.hasSendReceiptTarget) {
      return;
    }

    const hidden = !this.sendReceiptTarget.checked;

    if (this.hasReceiptMessageTarget) {
      this.receiptMessageTarget.classList.toggle('hide', hidden);
    }
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.classList.toggle('hide', hidden);
    }
  }

  #notifyPresets() {
    this.dispatch('messageChanged', {
      detail: { message: this.receiptMessageInputTarget.value },
    });
  }
}
